import { DOCUMENT, Location } from '@angular/common';
import { Directive, ElementRef, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';

import { TrackingEvents } from '../data/tracking-events.data';
import { ILink } from '../models/link.model';
import { GtmService } from '../services/gtm.service';
import { LinkService } from '../services/link.service';
import { SnowplowService } from '../services/snowplow.service';

/**
 * Decorates an `<a>` with project link semantics:
 *  - binds `href`/`rel`/`target`/`title` as host bindings, keeping the anchor
 *    natively focusable (no `href=""` workaround).
 *  - intercepts click to route via Angular Router or open a new window, and
 *    reports outbound file downloads to GTM / Snowplow.
 *  - emits `(linkSelect)` on activation so consumers avoid a literal `(click)`
 *    on the anchor; the host `href` binding alone provides keyboard activation,
 *    satisfying the template a11y lint rules without inline disables.
 */
@Directive({
    standalone: true,
    selector: 'a[siriusLink]',
    host: {
        '[attr.href]': 'href()',
        '[attr.rel]': 'rel()',
        '[attr.target]': 'target()',
        '[attr.title]': 'title()',
        '(click)': 'onNavigate($event)',
    },
})
export class LinkDirective {
    private _elRef = inject<ElementRef<HTMLAnchorElement>>(ElementRef);
    private _router = inject(Router);
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);
    private _location = inject(Location);
    private _document = inject(DOCUMENT);

    readonly siriusLink = input<ILink | undefined>(undefined);

    /**
     * Fires when the anchor is activated (mouse click or keyboard Enter, both
     * of which the browser routes through the DOM `click` event). The original
     * `MouseEvent` is forwarded so consumers can read it if needed.
     */
    readonly linkSelect = output<MouseEvent>();

    private readonly resolvedHref = computed<string | undefined>(() =>
        this._linkService.normalizeHref(this.siriusLink()?.href)
    );

    /**
     * Resolved href with the optional `#anchor` appended. `null` rather than
     * `''` so Angular drops the attribute; `''` resolves to the current
     * document URL on click.
     */
    readonly href = computed<string | null>(() => {
        const href = this.resolvedHref();
        if (!href) return null;

        const anchor = this.siriusLink()?.anchor;

        return anchor ? `${href}#${anchor}` : href;
    });

    readonly rel = computed<string | null>(() => this.siriusLink()?.rel ?? null);
    readonly target = computed<string | null>(() => this.siriusLink()?.target ?? null);
    readonly title = computed<string | null>(() => this.siriusLink()?.title ?? null);

    onNavigate(event: MouseEvent): void {
        const link = this.siriusLink();
        if (!link) return;

        // Notify consumers BEFORE we mutate routing / blur / preventDefault,
        // so their tracking / selection logic sees the raw user intent.
        this.linkSelect.emit(event);

        this.trackFileDownload(link);
        this._elRef.nativeElement.blur();

        if (link.target === '_blank' || this._linkService.isUrlAbsolute(this.href())) {
            return;
        }

        // Modified and non-primary clicks open a new tab or window; leave them to the browser.
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            return;
        }

        event.preventDefault();

        if (link.noRouting) return;

        this.navigate();
    }

    private navigate(): void {
        const href = this.href();
        if (!href) return;

        if (this.isSamePage(this.resolvedHref())) {
            const anchor = this.siriusLink()?.anchor;
            if (anchor) {
                this._location.replaceState(href, '', this._location.getState());
                this._document.getElementById(anchor)?.scrollIntoView();
            }
            return;
        }

        this._router.navigateByUrl(href);
    }

    /** Ignores the fragment and a trailing slash; the query must match exactly. */
    private isSamePage(target: string | undefined): boolean {
        return !!target && this._location.normalize(target.split('#')[0]) === this._location.path();
    }

    //#region tracking
    private trackFileDownload(link: ILink): void {
        if (!link.isFile) return;

        const el = this._elRef.nativeElement;
        const data = this._linkService.getTrackingData(el);
        const [fileName, rawExtension = ''] = this._linkService.getSlug(decodeURI(data.linkUrl)).split('.');
        const fileExtension = rawExtension.split('?')[0];

        const gtmPayload = {
            ...link.tracking?.parameters,
            link_url: data.linkUrl,
            link_classes: data.linkClasses,
            link_text: data.linkText,
            file_name: fileName,
            file_extension: fileExtension,
        };

        this._gtmService.track(TrackingEvents.gtm['fileDownload']([gtmPayload]));

        this._snowplowService.track('fileDownload', {
            file_extension: fileExtension,
            file_name: fileName,
            element_classes: data.linkClasses.split(' '),
            element_target: link.target,
            element_content: data.linkText,
            target_url: data.linkUrl,
            context: [],
        });
    }
    //#endregion
}
